import * as React from "react";
import { Button, Divider } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../components/antd/card/CardCollapsibleContent";
import { PolicyInput } from "../../../schema/types/policy/input";
import { inputToQuery, QueryMatchOptions } from "../../../schema/types/query/input";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request } from "../../../services/cpln";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { Table } from "../../../newcomponents/table/table";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { UserTableItem, getUserTableItem } from "../../../mst/kinds/user.helpers";
import { UserNameDescriptionColumn } from "../../../newcomponents/table/columns/wellKnown/userNameDescriptionColumn";
import { formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { store } from "../../../store";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpArrayObject } from "../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemQueryTerm } from "./CpArrayItemQueryTerm";

interface Props {}
export const PolicyQueryUser: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const query = useCpFormValue(`targetQuery`);
  const queryResult = useCpFormValue(`meta.queryResult.user.data`);
  const queryResultIsCollapsed = useCpFormValue(`meta.queryResult.user.isCollapsed`);

  const [isEvaluatingQuery, setIsEvaluatingQuery] = React.useState(false);
  async function evaluateQuery() {
    try {
      setIsEvaluatingQuery(true);
      const form = store.getState().form.instances[id];
      const values: PolicyInput = form.values as any;

      const queryApi = inputToQuery(values.targetQuery, "user");
      const { data } = await request({
        method: "post",
        url: `${homeLink("user")}/-query`,
        body: queryApi,
      });
      formSetValue(id, `meta.queryResult.user.data`, data);
      setIsEvaluatingQuery(false);
    } catch (e) {
      formSetValue(id, `meta.queryResult.user.data`, null);
      setIsEvaluatingQuery(false);
    }
  }

  const tableItems = (queryResult?.items || []).map((i) => getUserTableItem({ item: i }));

  return (
    <>
      <CardContent>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`targetQuery.isEnabled`}>Configure User Query</CpLabel>
            <CpSwitch name={`targetQuery.isEnabled`} />
          </div>
        </CpFormItem>
        <CardCollapsibleContent collapsed={!query.isEnabled}>
          <CpFormItem>
            <CpLabel name={`targetQuery.match`}>Match</CpLabel>
            <CpSelect name={`targetQuery.match`} options={QueryMatchOptions} style={{ width: 250 }} />
            <CpErrorWarning name={`targetQuery.match`} />
          </CpFormItem>
          <CpArrayObject
            name={`targetQuery.terms`}
            labels={[
              { label: "Type", span: 1 },
              { label: "Key", span: 2 },
              { label: "Op", span: 1 },
              { label: "Value", span: 2 },
            ]}
            label={`Terms`}
            factoryFn={() => ({ id: uuidv4(), key: "", termType: "tag", op: "=", value: "" })}
          >
            <CpArrayItemQueryTerm kind={`user`} />
          </CpArrayObject>
          <Divider />
          <CardTitle style={queryResultIsCollapsed ? { marginBottom: 0 } : undefined}>
            <div className={`flex items-baseline ${queryResultIsCollapsed ? "" : "mb-4"}`}>
              <div
                className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                onClick={() => {
                  formSetValue(id, `meta.queryResult.user.isCollapsed`, !queryResultIsCollapsed);
                }}
              >
                <CaretRightOutlined rotate={queryResultIsCollapsed ? 0 : 90} className={`self-center`} />
                <span>Query Evaluation</span>
              </div>
              <div className="flex-grow" />
            </div>
          </CardTitle>
          <CardCollapsibleContent collapsed={queryResultIsCollapsed}>
            <Button
              htmlType={`button`}
              block
              type={"default"}
              onClick={() => evaluateQuery()}
              disabled={isEvaluatingQuery}
              loading={isEvaluatingQuery}
            >
              Evaluate Query
            </Button>
            {queryResult ? (
              <Table<UserTableItem>
                tableId="policy-user-query-result"
                data={tableItems}
                hideSettings
                columns={[
                  UserNameDescriptionColumn({ enableSort: false }, { shouldForceTargetBlank: () => true }), //
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                ]}
              />
            ) : null}
          </CardCollapsibleContent>
        </CardCollapsibleContent>
      </CardContent>
    </>
  );
};
